import axios from "axios";
import config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Dispatch } from "interfaces/dispatch";
import { ThunkActionCreator } from "interfaces/thunk";
import { handleActions } from "redux-actions";
import { CREATE_PROJECT_REQUEST } from "state/create_project";

// Reducer Interface
export interface FileUploadState {
  readonly data?: FormData;
  readonly loading?: boolean;
  readonly hasError?: boolean;
  readonly errors?: string[];
  readonly content?: { fileList: string[] } | "OK";
  readonly chainInfo?: { chainUserMapping: any[] } | null;
}

export interface FileCheckUploadState {
  readonly loading?: boolean;
  readonly hasError?: boolean;
  readonly errors?: string[];
  readonly content?: any[];
}

// Actions Types
const UPLOAD_FILE_CHECK = (prefix: string) => `${prefix}_UPLOAD_FILE_CHECK`;
const UPLOAD_FILE_REQUEST = (prefix: string) => `${prefix}_UPLOAD_FILE_REQUEST`;
const UPLOAD_FILE = (prefix: string) => `${prefix}_UPLOAD_FILE`;
const UPLOAD_FILE_CHECK_SUCCEEDED = (prefix: string) => `${prefix}_UPLOAD_FILE_CHECK_SUCCEEDED`;
const UPLOAD_FILE_CHECK_FAILED = (prefix: string) => `${prefix}_UPLOAD_FILE_CHECK_FAILED`;
const UPLOAD_FILE_FAILED = (prefix: string) => `${prefix}_UPLOAD_FILE_FAILED`;
const UNSET_ERROR = (prefix: string) => `${prefix}_UNSET_ERROR`;
const RESET_UPLOAD = (prefix: string) => `${prefix}_RESET_UPLOAD`;
const FILE_UPLOAD_SASURL = (prefix: string) => `${prefix}FILE_UPLOAD_SASURL`;
const FILE_UPLOAD_SASURL_FAILED = (prefix: string) => `${prefix}FILE_UPLOAD_SASURL_FAILED`;
const UPDATE_UPLOAD_FILE_SUCCEEDED = (prefix: string) => `${prefix}UPDATE_UPLOAD_FILE_SUCCEEDED`;
const UPDATE_UPLOAD_FILE_FAILED = (prefix: string) => `${prefix}UPDATE_UPLOAD_FILE_FAILED`;

// Thunk Action Creator

const checkExistingFiles: ThunkActionCreator<Promise<void>> =
  (prefix: string, projectId: number, fileStatus?: string) => (dispatch: Dispatch) => {
    dispatch({
      type: UPLOAD_FILE_CHECK(prefix),
    });

    const url = `${config.API_SERVICE_URL}/api/upload-attachments/get-file-list`;
    const data = { projectId, fileStatus };
    return axios
      .post(url, data, getHeaders())
      .then((response) => {
        dispatch({
          type: UPLOAD_FILE_CHECK_SUCCEEDED(prefix),
          payload: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: UPLOAD_FILE_CHECK_FAILED(prefix),
          payload: [error],
        });
      });
  };

const getSASURLUpload: ThunkActionCreator<Promise<void>> =
  (
    prefix: string,
    projectId: number,
    chainId: string,
    fileStatus?: string,
    fileNames?: [],
    userId?: number,
    filetype?: string
  ) =>
  (dispatch: Dispatch) => {
    const url = `${config.API_SERVICE_URL}/api/upload-attachments/getsasurl`;
    const data = { projectId, fileStatus, filetype, fileNames, userId, chainId };
    return axios
      .post(url, data, getHeaders())
      .then((response) => {
        dispatch({
          type: FILE_UPLOAD_SASURL(prefix),
          payload: response.data,
        });
        return response.data;
      })
      .catch((error) => {
        dispatch({
          type: FILE_UPLOAD_SASURL_FAILED(prefix),
          payload: [error],
        });
      });
  };

const updateUploadedFiles: ThunkActionCreator<Promise<void>> =
  (
    projectId: number,
    fileStatus?: string,
    filename?: string,
    chainId?: number,
    uploadFrom?: string
  ) =>
  (dispatch: Dispatch) => {
    const url = `${config.API_SERVICE_URL}/api/upload-attachments/updateFileInfo`;
    const data = { projectId, fileStatus, filename, chainId, uploadFrom };
    return axios
      .post(url, data, getHeaders())
      .then((response) => {
        dispatch({
          type: UPDATE_UPLOAD_FILE_SUCCEEDED,
          payload: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: UPDATE_UPLOAD_FILE_FAILED,
          payload: [error],
        });
      });
  };

const uploadFile =
  (
    prefix: string,
    uploadUrl: string,
    options?: { offset?: number; timezone?: string }
  ): ThunkActionCreator<Promise<void>> =>
  ({ data }: FileUploadState) =>
  (dispatch: Dispatch) => {
    const flattenErrors = (input: any): string[] => {
      if (Array.isArray(input)) {
        return input.reduce((acc: string[], value: any) => acc.concat(flattenErrors(value)), []);
      }

      if (typeof input === "string" && input.trim()) {
        return [input.trim()];
      }

      return [];
    };

    const normalizeUploadErrors = (error: any): string[] => {
      const normalized: string[] = [];
      const responseData = error?.response?.data;

      normalized.push(...flattenErrors(responseData?.errors));

      if (typeof responseData?.message === "string" && responseData.message.trim()) {
        normalized.push(responseData.message.trim());
      }

      if (typeof responseData?.error === "string" && responseData.error.trim()) {
        normalized.push(responseData.error.trim());
      }

      if (error instanceof Error && error.message && error.message.trim()) {
        normalized.push(error.message.trim());
      }

      const uniqueErrors = Array.from(new Set(normalized));
      if (uniqueErrors.length > 0) {
        return uniqueErrors;
      }

      if (error instanceof Error && /Network Error|timeout/i.test(error.message)) {
        return ["Request Timed Out"];
      }

      return ["Upload failed. Please review and correct the file."];
    };

    dispatch({
      type: UPLOAD_FILE_REQUEST(prefix),
    });

    let url = uploadUrl;
    if (options && options.offset) {
      url = `${url}?offset=${options.offset}`;
      if (options.timezone) {
        url = `${url}&timezone=${options.timezone}`;
      }
    }
    return axios
      .post(url, data, getHeaders())
      .then((response) => {
        dispatch({ type: UPLOAD_FILE(prefix), payload: response.data });
      })
      .catch((error) => {
        const normalizedErrors = normalizeUploadErrors(error);
        dispatch({ type: UPLOAD_FILE_FAILED(prefix), payload: normalizedErrors });
      });
  };

const unsetError = (prefix: string) => (dispatch: Dispatch) => {
  dispatch({ type: UNSET_ERROR(prefix) });
};

const resetUpload = (prefix: string) => (dispatch: Dispatch) => {
  dispatch({ type: RESET_UPLOAD(prefix) });
};

const fileCheckFailed = (prefix: string, error: string) => (dispatch: Dispatch) => {
  dispatch({ type: UPLOAD_FILE_CHECK_FAILED(prefix), payload: [error] });
};

export {
  unsetError,
  fileCheckFailed,
  checkExistingFiles,
  uploadFile,
  resetUpload,
  getSASURLUpload,
  updateUploadedFiles,
};

const createFileUploadReducer = (prefix: string) => {
  // Actions
  const actions = {
    [CREATE_PROJECT_REQUEST]: () => ({ ...initialState }),
    [RESET_UPLOAD(prefix)]: () => ({
      content: [],
      loading: false,
      hasError: false,
      errors: [],
    }),
    [UNSET_ERROR(prefix)]: () => ({ ...initialState }),
    [UPLOAD_FILE_REQUEST(prefix)]: () => ({ loading: true }),
    [UPLOAD_FILE_CHECK(prefix)]: () => ({ loading: true }),
    [UPLOAD_FILE_CHECK_SUCCEEDED(prefix)]: (state: FileUploadState, action: AnyAction) => ({
      content: action.payload,
      loading: false,
      hasError: false,
      errors: [],
    }),
    [UPLOAD_FILE_CHECK_FAILED(prefix)]: (state: FileUploadState, action: AnyAction) => ({
      loading: false,
      hasError: true,
      errors: action.payload,
    }),
    [UPLOAD_FILE_REQUEST(prefix)]: () => ({ loading: true }),
    [UPLOAD_FILE(prefix)]: (state: FileUploadState, action: AnyAction): FileUploadState => ({
      content: action.payload,
      loading: false,
      hasError: false,
      errors: [],
      chainInfo: action.payload?.chainInfo ?? null
    }),
    [UPLOAD_FILE_FAILED(prefix)]: (state: FileUploadState, action: AnyAction) => ({
      loading: false,
      hasError: true,
      errors: action.payload,
    }),
    [FILE_UPLOAD_SASURL(prefix)]: (state: FileUploadState, action: AnyAction): FileUploadState => ({
      content: action.payload,
      loading: false,
      hasError: false,
      errors: [],
    }),
    [FILE_UPLOAD_SASURL_FAILED(prefix)]: (state: FileUploadState, action: AnyAction) => ({
      loading: false,
      hasError: true,
      errors: action.payload,
    }),
  };

  // Initial State
  const initialState: FileUploadState = {
    loading: false,
    hasError: false,
    errors: [],
  };

  // Reducer
  return handleActions<FileUploadState, AnyAction>(actions, initialState);
};

export default createFileUploadReducer;
